import React, { useState } from "react";
import { Modal, Form, Button, ListGroup, Spinner, InputGroup, Badge, Row, Col, Accordion } from "react-bootstrap";
import { FaSearch, FaTimes, FaInfoCircle } from "react-icons/fa";
import ProviderCard from "./cards/ProviderCard";
import { searchProvidersByTerm } from "./services/providerService";
import {
    fetchCareTeamPractitioners,
    fetchOrganizationsPractitioners,
    fetchRelatedPersonPractitioners,
    fetchCommunicationSenders,
    fetchCarePlansForPractitioner
} from "./services/providerService";
import { useDispatch, useSelector } from "react-redux";
import { useStore } from "react-redux";
import OverlayTrigger from "react-bootstrap/OverlayTrigger";
import tooltipRegistry from "./tooltips/tooltipRegistry";
import GenericFHIRViewerModal from "./cards/GenericFHIRViewerModal";

const DEFAULT_SEARCH_TYPES = {
    providers: true,
    organizations: false,
    careTeams: false,
    relatedPersons: false,
    communications: false
};

const CATEGORY_LABELS = {
    providers: "Providers",
    organizations: "Organizations",
    careTeams: "Care Teams",
    relatedPersons: "Related Persons",
    communications: "Communications"
};



/**
 * ProviderSearch component
 * - Allows searching for providers by name or for a patient's circle of care.
 * - Calls onRecipientsChange(recipients) when recipients list changes.
 * - Optionally accepts a patientId to search for circle of care.
 */
function ProviderSearch({ fhirClient, patientId, onRecipientsChange, recipients = [] }) {
    const [query, setQuery] = useState("");
    const [results, setResults] = useState({
        providers: [],
        organizations: [],
        careTeams: [],
        relatedPersons: [],
        communications: []
    });
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");
    //const [recipients, setRecipients] = useState([]);
    const [searchTypes, setSearchTypes] = useState(DEFAULT_SEARCH_TYPES);
    const [searchMode, setSearchMode] = useState("provider"); // "provider" or "circle"
    const [activeAccordionKeys, setActiveAccordionKeys] = useState([]);
    const [selectedForView, setSelectedForView] = useState(null);
    const [showFhirViewerModal, setShowFhirViewerModal] = useState(false);
    const [fhirViewerResource, setFhirViewerResource] = useState(null);
    const dispatch = useDispatch();
    const store = useStore();
    const [errorModalShow, setErrorModalShow] = useState(false);
    const [errorModalMessage, setErrorModalMessage] = useState("");

    // Clear provider search box when switching to Circle of Care
    const handleSearchModeChange = (mode) => {
        setSearchMode(mode);

        if (mode === "circle") {
            setQuery("");

            // Enable all circle-relevant categories by default
            setSearchTypes({
                providers: false, // provider search is manual
                organizations: true,
                careTeams: true,
                relatedPersons: true,
                communications: true
            });
        }

        if (mode === "provider") {
            setSearchTypes({
                providers: true,
                organizations: false,
                careTeams: false,
                relatedPersons: false,
                communications: false
            });
        }
    };

    const handleTypeChange = (type) => {
        setSearchTypes((prev) => ({
            ...prev,
            [type]: !prev[type]
        }));
    };

    const handleSearch = async (e) => {
        e.preventDefault();
        setLoading(true);
        setError("");
        setResults({
            providers: [],
            organizations: [],
            careTeams: [],
            relatedPersons: [],
            communications: []
        });

        try {
            const newResults = {
                providers: [],
                organizations: [],
                careTeams: [],
                relatedPersons: [],
                communications: []
            };

            if (searchMode === "provider") {
                if (searchTypes.providers) {
                    const providers = await searchProvidersByTerm(query, fhirClient);
                    newResults.providers = providers;
                }
            } else {
                if (searchTypes.organizations && patientId) {
                    const practitioners = await fetchOrganizationsPractitioners(patientId, fhirClient, store.getState, store.dispatch);
                    newResults.organizations = practitioners;
                }
                if (searchTypes.careTeams && patientId) {
                    const practitioners = await fetchCareTeamPractitioners(patientId, fhirClient, store.getState, store.dispatch);
                    newResults.careTeams = practitioners;
                }
                if (searchTypes.relatedPersons && patientId) {
                    const practitioners = await fetchRelatedPersonPractitioners(patientId, fhirClient, store.getState, store.dispatch);
                    newResults.relatedPersons = practitioners;
                }

                if (searchTypes.communications && patientId) {
                    const practitioners = await fetchCommunicationSenders(patientId, fhirClient, store.getState, store.dispatch);
                    newResults.communications = practitioners;
                }

            }

            setResults(newResults);

            // Expand ribbons with results
            const keysWithResults = Object.keys(newResults).filter(
                (cat) =>
                    (searchMode === "provider"
                        ? cat === "providers"
                        : cat !== "providers" && searchTypes[cat]) &&
                    Array.isArray(newResults[cat]) &&
                    newResults[cat].length > 0
            );
            setActiveAccordionKeys(keysWithResults);
        } catch (err) {
            console.log("Error searching resources:", err);
            setError("Failed to search resources.", err);
        }
        setLoading(false);
    };

    const handleResultCheckbox = (resource, checked) => {
        const exists = recipients.some(
            r => r.id === resource.id && r.resourceType === resource.resourceType
        );

        if (checked && !exists) {
            onRecipientsChange([...recipients, resource]);
        } else if (!checked && exists) {
            const updated = recipients.filter(
                r => !(r.id === resource.id && r.resourceType === resource.resourceType)
            );
            onRecipientsChange(updated);
        }
    };

    // Display name helper
    const displayName = (resource) => {
        //console.log(resource);
        if (resource.resourceType === "Practitioner") {
            return (
                resource.name?.[0]?.text ||
                [resource.name?.[0]?.given?.join(" "), resource.name?.[0]?.family].filter(Boolean).join(" ") ||
                resource.id
            );
        }
        if (resource.resourceType === "Organization") {
            return resource.name || resource.id;
        }
        if (resource.resourceType === "CareTeam") {
            return resource.name || `CareTeam/${resource.id}`;
        }
        if (resource.resourceType === "RelatedPerson") {
            return (
                resource.name?.[0]?.text ||
                [resource.name?.[0]?.given?.join(" "), resource.name?.[0]?.family].filter(Boolean).join(" ") ||
                resource.id
            );
        }
        if (resource.resourceType === "Communication") {
            return resource.topic?.text || `Communication/${resource.id}`;
        }
        return resource.id;
    };

    // Only show accordions for checked categories (excluding providers for circle of care)
    const activeCategories = Object.keys(searchTypes).filter((cat) =>
        searchMode === "provider"
            ? cat === "providers"
            : cat !== "providers" && searchTypes[cat]
    );

    // Helper to show the first CarePlan for a practitioner in a modal
    const handleCareTeamInfoClick = async (practitioner) => {
        if (!patientId || !practitioner?.id) {
            setErrorModalMessage("Missing patient or practitioner ID.");
            setErrorModalShow(true);
            return;
        }

        const carePlans = await fetchCarePlansForPractitioner(patientId, practitioner.id, fhirClient);
        if (carePlans.length > 0) {
            setFhirViewerResource(carePlans);
            setShowFhirViewerModal(true);
        } else {
            setErrorModalMessage("No CarePlans found for this provider.");
            setErrorModalShow(true);
        }
    };

    return (
        <div>
            {/* Recipient type radio buttons */}
            <Form className="mb-2">
                <Form.Check
                    type="radio"
                    id="search-mode-provider"
                    label="Provider"
                    name="searchMode"
                    value="provider"
                    checked={searchMode === "provider"}
                    onChange={() => handleSearchModeChange("provider")}
                    inline
                />
                <OverlayTrigger placement="bottom" overlay={tooltipRegistry.circleOfCare}>
                    <Form.Check
                        type="radio"
                        id="search-mode-circle"
                        label="Circle of Care"
                        name="searchMode"
                        value="circle"
                        checked={searchMode === "circle"}
                        onChange={() => handleSearchModeChange("circle")}
                        inline
                    />
                </OverlayTrigger>

            </Form>
            {searchMode === "provider" && (
                <Form onSubmit={handleSearch} className="mb-2">
                    <InputGroup>
                        <Form.Control
                            type="text"
                            placeholder={patientId ? "Search Providers" : "Search providers by name..."}
                            value={query}
                            onChange={(e) => setQuery(e.target.value)}
                            disabled={loading}
                        />
                        <Button type="submit" variant="primary" disabled={loading}>
                            <FaSearch />
                        </Button>
                    </InputGroup>
                </Form>
            )}

            {searchMode === "circle" && (
                <Form onSubmit={handleSearch} className="mb-2">
                    <Row className="mt-3">
                        <Col xs={12}>
                            <Form.Check
                                inline
                                label="Organizations"
                                type="checkbox"
                                checked={searchTypes.organizations}
                                onChange={() => handleTypeChange("organizations")}
                            />
                            <Form.Check
                                inline
                                label="CareTeams"
                                type="checkbox"
                                checked={searchTypes.careTeams}
                                onChange={() => handleTypeChange("careTeams")}
                            />
                            <Form.Check
                                inline
                                label="RelatedPersons"
                                type="checkbox"
                                checked={searchTypes.relatedPersons}
                                onChange={() => handleTypeChange("relatedPersons")}
                            />
                            <Form.Check
                                inline
                                label="Communications"
                                type="checkbox"
                                checked={searchTypes.communications}
                                onChange={() => handleTypeChange("communications")}
                            />

                        </Col>
                        <Button type="submit" variant="primary" disabled={loading}>SEARCH</Button>
                    </Row>
                </Form>
            )}
            {loading && <Spinner animation="border" size="sm" />}
            {error && <div className="text-danger">{error}</div>}



            {/* Accordions for each active category */}
            <Accordion
                activeKey={activeAccordionKeys}
                onSelect={(key) => {
                    if (!key) return;
                    setActiveAccordionKeys((prev) =>
                        prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key]
                    );
                }}
            >
                {activeCategories.map((cat) => (
                    <Accordion.Item eventKey={cat} key={cat}>
                        <Accordion.Header>
                            {CATEGORY_LABELS[cat] || cat.charAt(0).toUpperCase() + cat.slice(1)}{" "}
                            <span className="text-muted">&nbsp;({(results[cat] || []).length})</span>
                        </Accordion.Header>
                        <Accordion.Body>
                            <ListGroup>
                                {(results[cat] || []).length === 0 && (
                                    <ListGroup.Item className="text-muted">No results</ListGroup.Item>
                                )}
                                {(results[cat] || []).map((resource) => (
                                    <ListGroup.Item
                                        key={resource.id + resource.resourceType}
                                        action
                                        style={{ display: "flex", alignItems: "center", cursor: "pointer" }}
                                        onClick={e => {
                                            // Only trigger if not clicking the checkbox or info icon
                                            if (
                                                e.target.type !== "checkbox" &&
                                                !e.target.closest(".provider-info-icon")
                                            ) {
                                                setSelectedForView(resource);
                                            }
                                        }}
                                    >
                                        <Form.Check
                                            type="checkbox"
                                            className="me-2"
                                            checked={recipients.some(
                                                (r) => r.id === resource.id && r.resourceType === resource.resourceType
                                            )}
                                            onChange={(e) => handleResultCheckbox(resource, e.target.checked)}
                                            onClick={(e) => e.stopPropagation()}
                                        />
                                        <span style={{ flex: 1 }}>
                                            {displayName(resource)}
                                            <span className="ms-2 text-muted">[{resource.resourceType}]</span>
                                        </span>
                                        {cat === "careTeams" ? (
                                            <FaInfoCircle
                                                className="ms-2 text-primary provider-info-icon"
                                                style={{ cursor: "pointer" }}
                                                title="Show CarePlan"
                                                onClick={async e => {
                                                    e.stopPropagation();
                                                    await handleCareTeamInfoClick(resource);
                                                }}
                                            />
                                        ) : (
                                            <FaInfoCircle
                                                className="ms-2 text-primary provider-info-icon"
                                                style={{ cursor: "pointer" }}
                                                title="Info"
                                                onClick={e => {
                                                    e.stopPropagation();
                                                    setFhirViewerResource(resource);
                                                    setShowFhirViewerModal(true);
                                                }}
                                            />
                                        )}
                                    </ListGroup.Item>
                                ))}
                            </ListGroup>
                        </Accordion.Body>
                    </Accordion.Item>
                ))}
            </Accordion>

            {/* ProviderCard under the ribbon results */}
            <div className="mt-3">
                <ProviderCard
                    title="Selected Provider"
                    providerData={selectedForView || recipients.find(r => r.resourceType === "Practitioner") || null}
                    onSelectProvider={() => { }}
                    showSearch={false}
                    fhirClient={fhirClient}
                />
            </div>

            {/* GenericFHIRViewerModal for resource details */}
            <GenericFHIRViewerModal
                show={showFhirViewerModal}
                onClose={() => setShowFhirViewerModal(false)}
                resource={fhirViewerResource}
                title={fhirViewerResource ? displayName(fhirViewerResource) : "Resource Details"}
                fhirClient={fhirClient}
            />

            <Modal show={errorModalShow} onHide={() => setErrorModalShow(false)} centered>
                <Modal.Header closeButton>
                    <Modal.Title>Provider Search Error</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <p>{errorModalMessage}</p>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => setErrorModalShow(false)}>
                        Close
                    </Button>
                </Modal.Footer>
            </Modal>
        </div>


    );

}

export default ProviderSearch;